import { BodyText, List, ListItem, Section } from '../List';
import { CookieInformationLabels, DeviceStorage } from '@usercentrics/cmp-browser-sdk';
import { Fragment, FunctionalComponent, h } from 'preact';
import ExpandableLink from '../ExpandableLink/ExpandableLink';
import { PaddedTitle } from '../VendorDescription/VendorDescription.styles';
import { VerticalSpacer } from '../Spacers/VerticalSpacer';
import { convertCookieMaxAge } from './helpers';
import { memo } from 'preact/compat';

interface Props {
  cookieInformationLabels: CookieInformationLabels;
  cookieMaxAgeSeconds: number | null;
  cookieRefresh: boolean | null;
  deviceStorage: DeviceStorage | null;
  deviceStorageDisclosureUrl: string | null;
  onToggleExpandable?: () => void;
  storageInfoAnchorLabel: string;
  usesCookies: boolean | null;
  usesNonCookieAccess: boolean | null;
  isNonIabVendor: boolean | null;
}

const StorageInfo: FunctionalComponent<Props> = memo(
  ({
    cookieInformationLabels,
    cookieMaxAgeSeconds,
    cookieRefresh,
    deviceStorage,
    deviceStorageDisclosureUrl,
    onToggleExpandable,
    storageInfoAnchorLabel,
    usesCookies,
    usesNonCookieAccess,
    isNonIabVendor,
  }) => {
    const hasCookieMaxAgeSeconds = cookieMaxAgeSeconds !== null;
    const hasUsesNonCookieAccess = usesNonCookieAccess !== null;
    const hasDeviceStorage = deviceStorage?.disclosures && deviceStorage.disclosures.length > 0;
    const hasDisclosureUrl = deviceStorageDisclosureUrl && deviceStorageDisclosureUrl !== '';

    if (!usesCookies && !isNonIabVendor) {
      return null;
    }

    if (!hasCookieMaxAgeSeconds && !hasUsesNonCookieAccess && !hasDeviceStorage && !hasDisclosureUrl) {
      return null;
    }

    return (
      <Section>
        {(hasCookieMaxAgeSeconds || hasUsesNonCookieAccess) && (
          <Fragment>
            <PaddedTitle>{cookieInformationLabels.title}</PaddedTitle>
            {isNonIabVendor && <BodyText>{cookieInformationLabels.description}</BodyText>}
            <List>
              {usesCookies !== null && (
                <ListItem showBulletPoints>
                  {`${cookieInformationLabels.usesCookies}: ${
                    usesCookies ? `${cookieInformationLabels.yes}` : `${cookieInformationLabels.no}`
                  }`}
                </ListItem>
              )}
              {hasCookieMaxAgeSeconds && (
                <ListItem showBulletPoints>
                  {`${cookieInformationLabels.maximumAge}: ${convertCookieMaxAge(
                    cookieMaxAgeSeconds,
                    cookieInformationLabels,
                  )}`}
                </ListItem>
              )}
              {cookieRefresh !== null && (
                <ListItem showBulletPoints>
                  {`${cookieInformationLabels.cookieRefresh}: ${
                    cookieRefresh ? `${cookieInformationLabels.yes}` : `${cookieInformationLabels.no}`
                  }`}
                </ListItem>
              )}
              {hasUsesNonCookieAccess !== null && (
                <ListItem showBulletPoints>
                  {`${cookieInformationLabels.nonCookieStorage}: ${
                    usesNonCookieAccess ? `${cookieInformationLabels.yes}` : `${cookieInformationLabels.no}`
                  }`}
                </ListItem>
              )}
            </List>
          </Fragment>
        )}
        {(hasDeviceStorage || hasDisclosureUrl) && (
          <Fragment>
            <VerticalSpacer custom={12} />
            <ExpandableLink
              ariaLabel={storageInfoAnchorLabel}
              onToggleExpandable={onToggleExpandable}
              deviceStorage={deviceStorage}
              disclosureUrl={deviceStorageDisclosureUrl}
              labels={cookieInformationLabels}
              isNonIabVendor={isNonIabVendor}
            />
          </Fragment>
        )}
      </Section>
    );
  },
);

export default StorageInfo;
